import DataTable from "../../components/common/DataTable";
import React from "react";
import { Progress } from "@mantine/core";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { notifications } from "@mantine/notifications";
import { AnalyticsPageLayout, StatCard, ChartCard, TOOLTIP_STYLE, chartProps } from "./AnalyticsLayout";
import { CreditCard } from "lucide-react";

const paymentMethods = [
  { name: "Credit Card", value: 45, color: "#3b82f6" },
  { name: "Debit Card", value: 30, color: "#10b981" },
  { name: "Wallet", value: 15, color: "#f59e0b" },
  { name: "PayPal", value: 10, color: "#8b5cf6" },
];

const paymentAnalytics = [
  { key: "1", gateway: "Stripe", transactions: 45230, successful: 44812, failed: 418, successRate: 99.08, revenue: 562500, fees: 16875 },
  { key: "2", gateway: "PayPal", transactions: 28450, successful: 27956, failed: 494, successRate: 98.27, revenue: 355625, fees: 12447 },
  { key: "3", gateway: "Razorpay", transactions: 18920, successful: 18654, failed: 266, successRate: 98.59, revenue: 236500, fees: 7396 },
  { key: "4", gateway: "Square", transactions: 12340, successful: 12108, failed: 232, successRate: 98.12, revenue: 154250, fees: 4625 },
];

const paymentColumns = [
  { title: "Payment Gateway", dataIndex: "gateway", key: "gateway" },
  { title: "Total Transactions", dataIndex: "transactions", key: "transactions", render: (t) => t.toLocaleString() },
  { title: "Successful", dataIndex: "successful", key: "successful", render: (t) => t.toLocaleString() },
  { title: "Failed", dataIndex: "failed", key: "failed", render: (t) => <span className="text-red-500">{t}</span> },
  { title: "Success Rate", dataIndex: "successRate", key: "successRate", render: (t) => <Progress value={Math.round(t)} color="green" size="sm" style={{ width: 60 }} /> },
  { title: "Revenue", dataIndex: "revenue", key: "revenue", render: (t) => `Tsh ${t.toLocaleString()}` },
  { title: "Fees", dataIndex: "fees", key: "fees", render: (t) => `Tsh ${t.toLocaleString()}` },
];

const paymentTrendData = [
  { day: "Mon", completed: 8900, pending: 120, failed: 45 },
  { day: "Tue", completed: 9200, pending: 105, failed: 38 },
  { day: "Wed", completed: 10100, pending: 140, failed: 52 },
  { day: "Thu", completed: 9800, pending: 125, failed: 41 },
  { day: "Fri", completed: 11200, pending: 160, failed: 58 },
];

const PaymentAnalyticsPage = () => {
  const handleExport = (format) => {
    notifications.show({ message: `Exporting as ${format.toUpperCase()}...`, color: "green" });
  };

  return (
    <AnalyticsPageLayout
      icon={CreditCard}
      title="Payment Analytics"
      subtitle="Payment transactions & gateway performance"
      onExport={handleExport}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Transactions" value={104940} suffix="" />
        <StatCard title="Success Rate" value={98.6} suffix="%" />
        <StatCard title="Total Revenue" prefix="Tsh " value={1308875} />
        <StatCard title="Total Fees" prefix="Tsh " value={41343} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="Payment Methods Distribution">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={paymentMethods} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name} ${value}%`} labelLine={false}>
                  {paymentMethods.map((e, i) => <Cell key={i} fill={e.color} stroke="#0a0a0a" strokeWidth={2} />)}
                </Pie>
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n, p) => [`${v}%`, p.payload.name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
        <ChartCard title="Daily Payment Trends">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={paymentTrendData}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="day" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" />
                <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending" />
                <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <ChartCard title="Payment Gateway Performance">
        <DataTable dataSource={paymentAnalytics} columns={paymentColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
      </ChartCard>
    </AnalyticsPageLayout>
  );
};

export default PaymentAnalyticsPage;
